import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { mergeRegister } from '@lexical/utils';
import {
  $getNodeByKey, $getSelection, $isNodeSelection,
  CLICK_COMMAND, COMMAND_PRIORITY_LOW, KEY_BACKSPACE_COMMAND, KEY_DELETE_COMMAND,
} from 'lexical';
import { ImageOff, UploadCloud } from 'lucide-react';
import { getDecryptedObjectUrl } from '../../../utils/decryptedMediaCache';
import { isDocVideoMime } from '../../../utils/sanitizeHtml';
import {
  getPreview, getUploadError, getUploadStatus, retryUpload, subscribeUploads,
} from '../../../services/documentAttachmentService';
import { readCiphertext, readBlobBytes } from '../../../services/documentAttachmentOutbox';
import { decryptMediaBuffer } from '../../../utils/docMediaCrypto';
import { ImageLightbox } from '../../ImageLightbox';

// Floor for a resize drag: an image dragged to nothing becomes a sliver nobody
// can grab again, and deleting it is then the only way out.
const MIN_IMAGE_WIDTH = 64;

const nullSnapshot = () => null;

/**
 * The React face of a DocImageNode (see that file for the schema).
 *
 * The OUTER box always reserves the image's aspect ratio from the node's
 * stored w/h (width: w px capped at 100%), so none of the four states —
 * decrypting-shimmer, decrypted <img>, decrypt-failed, pending-upload — ever
 * changes the document's layout when it swaps in. Without dimensions the box
 * falls back to a fixed-height placeholder until the real <img> provides its
 * own size.
 *
 * States:
 *  - src set   -> fetch+decrypt via the shared LRU (decryptedMediaCache);
 *                 shimmer while in flight, then the <img>. Failure shows
 *                 "Unable to decrypt image" with a Retry that simply calls
 *                 again — the cache never keeps failed entries, so retrying
 *                 refetches for real.
 *  - src empty -> PENDING upload. When THIS session staged the upload
 *                 (services/documentAttachmentService knows the pendingRef),
 *                 the box shows the local compressed preview plus a status
 *                 ribbon driven by useSyncExternalStore — 'error' adds a
 *                 Retry that re-enqueues the SAME ciphertext. With no staged
 *                 preview (app restarted mid-upload) it asks the PERSISTENT
 *                 OUTBOX for the queued ciphertext and decrypts it locally —
 *                 a decrypt-once path per mount, NOT decryptedMediaCache
 *                 (that LRU is keyed by ciphertext URL, which a pending
 *                 upload does not have yet). Only when the outbox has
 *                 nothing either (the node synced from ANOTHER device, whose
 *                 outbox holds the bytes) does it fall back to the reserved
 *                 box with the muted "Waiting for upload…" label.
 *
 * Selection/removal is PageLinkComponent's exact pattern: selection rides
 * Lexical's own CLICK_COMMAND pipeline (a React onClick would be immediately
 * overridden by the editor's click handler), and Backspace/Delete remove the
 * node only while it is part of a NodeSelection.
 */
export default function DocImageComponent({ nodeKey, src, pendingRef, k, mime, name, w, h, alt, width }) {
  const [editor] = useLexicalComposerContext();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const boxRef = useRef(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // Pending-upload state, straight from the in-memory upload registry. Both
  // snapshots are value-stable strings (or null for a ref this session never
  // staged — the post-restart case), so useSyncExternalStore re-renders only
  // on real transitions.
  const uploadStatus = useSyncExternalStore(
    subscribeUploads,
    pendingRef ? () => getUploadStatus(pendingRef) : nullSnapshot
  );
  const preview = useSyncExternalStore(
    subscribeUploads,
    pendingRef ? () => getPreview(pendingRef) : nullSnapshot
  );
  // Post-restart pending preview: decrypted from the outbox's queued
  // ciphertext when the session has no staged preview object URL. Minted per
  // mount and revoked on cleanup (unlike the src path, whose URLs the LRU
  // cache owns).
  const [outboxPreviewUrl, setOutboxPreviewUrl] = useState(null);

  useEffect(() => {
    if (src || !pendingRef || preview) return undefined;
    let alive = true;
    let mintedUrl = null;
    (async () => {
      try {
        const blob = await readCiphertext(pendingRef);
        if (!blob || !alive) return;
        const bytes = await readBlobBytes(blob);
        const decrypted = await decryptMediaBuffer(k, bytes, mime);
        if (!alive) return;
        mintedUrl = URL.createObjectURL(decrypted);
        setOutboxPreviewUrl(mintedUrl);
      } catch (err) {
        // No bytes / undecryptable: the waiting box below is the fallback.
        console.warn('[docs] outbox preview unavailable:', err?.message || err);
      }
    })();
    return () => {
      alive = false;
      if (mintedUrl) {
        try {
          URL.revokeObjectURL(mintedUrl);
        } catch {
          // jsdom lacks revokeObjectURL — nothing to release there anyway
        }
      }
      setOutboxPreviewUrl(null);
    };
  }, [src, pendingRef, preview, k, mime]);

  useEffect(() => {
    if (!src) return undefined;
    let alive = true;
    setFailed(false);
    getDecryptedObjectUrl(src, k, mime)
      .then((u) => {
        if (alive) setObjectUrl(u);
      })
      .catch((e) => {
        console.warn('[docs] image decrypt failed:', e?.message || e);
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [src, k, mime, attempt]);

  const onDelete = useCallback(
    (event) => {
      if (isSelected && $isNodeSelection($getSelection())) {
        if (event) event.preventDefault();
        const node = $getNodeByKey(nodeKey);
        if (node) node.remove();
        return true;
      }
      return false;
    },
    [isSelected, nodeKey]
  );

  // Fullscreen preview, the SAME component chat uses (ImageLightbox), so an
  // image opens the same way wherever you meet one in the app.
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // Width during a drag, before it is committed to the node. Kept in React
  // state rather than written to Lexical on every pointermove: each write is a
  // history entry and a sync record, and a drag produces hundreds.
  const [liveWidth, setLiveWidth] = useState(null);
  const dragRef = useRef(null);

  const onResizeStart = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      const rect = boxRef.current?.getBoundingClientRect();
      if (!rect) return;
      dragRef.current = { startX: event.clientX, startW: rect.width };
      setLiveWidth(Math.round(rect.width));

      const onMove = (e) => {
        const d = dragRef.current;
        if (!d) return;
        // MIN_IMAGE_WIDTH so an image cannot be dragged to nothing and become
        // unfindable in the document; the max is the editor's own width, which
        // maxWidth:100% already enforces visually.
        setLiveWidth(Math.max(MIN_IMAGE_WIDTH, Math.round(d.startW + (e.clientX - d.startX))));
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        const committed = dragRef.current;
        dragRef.current = null;
        if (!committed) return;
        // ONE editor.update at the end of the drag: one history entry to undo,
        // one record to sync.
        setLiveWidth((finalW) => {
          if (finalW != null) {
            editor.update(() => {
              const node = $getNodeByKey(nodeKey);
              if (node && typeof node.setWidth === 'function') node.setWidth(finalW);
            });
          }
          return null;
        });
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [editor, nodeKey]
  );

  const onClickCommand = useCallback(
    (event) => {
      if (boxRef.current && (event.target === boxRef.current || boxRef.current.contains(event.target))) {
        if (!event.shiftKey) clearSelection();
        setSelected(true);
        return true;
      }
      return false;
    },
    [clearSelection, setSelected]
  );

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand(CLICK_COMMAND, onClickCommand, COMMAND_PRIORITY_LOW),
        editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
        editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW)
      ),
    [editor, onClickCommand, onDelete]
  );

  const hasDims = w != null && h != null;
  // The user's resize wins over the natural width; the aspect ratio always
  // comes from the NATURAL dimensions, so resizing never distorts the image
  // and a second resize does not compound against the first.
  const displayWidth = liveWidth != null ? liveWidth : width;
  const boxStyle = hasDims
    ? {
        aspectRatio: `${w} / ${h}`,
        width: `${displayWidth != null ? displayWidth : w}px`,
        maxWidth: '100%',
      }
    : displayWidth != null
      ? { width: `${displayWidth}px`, maxWidth: '100%' }
      : undefined;
  const pending = !src;
  const isVideo = isDocVideoMime(mime);
  const showImg = Boolean(src && objectUrl && !failed);
  const localPreview = preview || outboxPreviewUrl;
  const showPreview = Boolean(pending && localPreview);

  return (
    <div
      ref={boxRef}
      className={
        `lx-doc-image${isSelected ? ' lx-doc-image--selected' : ''}` +
        `${!showImg && !showPreview && !hasDims ? ' lx-doc-image--fallback-box' : ''}`
      }
      style={boxStyle}
      data-pending-ref={pendingRef || undefined}
    >
      {showImg && !isVideo && (
        <img
          className="lx-doc-image-img lx-doc-image-img--clickable"
          src={objectUrl}
          alt={alt || name || ''}
          onClick={() => setLightboxOpen(true)}
        />
      )}
      {/* A video (card 7de79076): the same encrypted media, decrypted to a
          blob URL and played with the browser's own controls. */}
      {showImg && isVideo && (
        <video
          className="lx-doc-image-img lx-doc-video"
          src={objectUrl}
          controls
          preload="metadata"
          playsInline
          aria-label={alt || name || 'Video'}
        />
      )}
      {showPreview && !isVideo && <img className="lx-doc-image-img" src={localPreview} alt={alt || name || ''} />}
      {showPreview && isVideo && (
        <video className="lx-doc-image-img lx-doc-video" src={localPreview} controls preload="metadata" playsInline muted />
      )}
      {/* Resize grip, revealed on hover rather than on selection: the
          selection ring was removed (clicking an image should open it, not
          decorate it), so hover is what is left to advertise that the image
          can be resized. Only offered once there is a real image — there is
          nothing to size while bytes are still uploading. */}
      {(showImg || showPreview) && (
        <span
          className="lx-doc-image-resize"
          role="separator"
          aria-label="Resize image"
          onPointerDown={onResizeStart}
        />
      )}
      {lightboxOpen && objectUrl && (
        <ImageLightbox
          src={objectUrl}
          alt={alt || name || ''}
          onClose={() => setLightboxOpen(false)}
        />
      )}
      {pending && !showPreview && (
        <div className="lx-doc-image-state">
          <UploadCloud size={16} aria-hidden />
          <span>Waiting for upload…</span>
        </div>
      )}
      {pending && uploadStatus === 'uploading' && (
        <div className="lx-doc-image-ribbon">
          <UploadCloud size={12} aria-hidden />
          <span>Uploading…</span>
        </div>
      )}
      {pending && uploadStatus === 'error' && (
        <div className="lx-doc-image-ribbon lx-doc-image-ribbon--error">
          {/* The WHY matters: 'HTTP 404' (server without the endpoint),
              'HTTP 503' (storage unconfigured) and 'network' need different
              humans to act — a bare 'Upload failed' sent a real report on a
              goose chase. */}
          <span>{`Upload failed${getUploadError(pendingRef) ? ` (${getUploadError(pendingRef)})` : ''}`}</span>
          <button
            type="button"
            className="lx-doc-image-retry"
            onClick={() => retryUpload(pendingRef)}
          >
            Retry
          </button>
        </div>
      )}
      {!pending && failed && (
        <div className="lx-doc-image-state">
          <ImageOff size={16} aria-hidden />
          <span>{isVideo ? 'Unable to decrypt video' : 'Unable to decrypt image'}</span>
          <button
            type="button"
            className="lx-doc-image-retry"
            onClick={() => {
              // The cache dropped the failed entry, so bumping `attempt`
              // re-runs the effect and genuinely refetches.
              setObjectUrl(null);
              setAttempt((n) => n + 1);
            }}
          >
            Retry
          </button>
        </div>
      )}
      {!pending && !failed && !objectUrl && (
        <div className="lx-doc-image-shimmer" aria-label={isVideo ? 'Decrypting video…' : 'Decrypting image…'} />
      )}
    </div>
  );
}
